import * as Sentry from "@sentry/angular";
import { Injectable, inject } from "@angular/core";
import { LoadedEvent, TranslocoService } from "@jsverse/transloco";

// Reports translation health to Sentry: keys requested but missing, and scopes whose
// translation file failed to load. Kept separate from ObservabilityService (the generic
// Sentry lifecycle facade) because tracking Transloco scope state is its own concern.
@Injectable({
    providedIn: "root",
})
export class I18nObservabilityService {
    private readonly transloco = inject(TranslocoService);

    // Scopes whose translation files Transloco has already loaded (from events$). Used to
    // tell a real typo (scope loaded) from the load race (scope not loaded yet).
    private readonly loadedScopes = new Set<string>();
    private started = false;

    // Called from an APP_INITIALIZER so the subscription is in place from bootstrap. The
    // report* methods no-op until Sentry is init'd (ObservabilityService.initGlobal).
    startMonitoring(): void {
        if (this.started) return;
        this.started = true;

        this.transloco.events$.subscribe((event) => {
            if (event.type === "translationLoadSuccess") {
                this.loadedScopes.add(event.payload.scope ?? "");
            } else if (event.type === "translationLoadFailure") {
                this.reportScopeLoadFailure(event.payload);
            }
        });
    }

    // Whether the scope's translation file has loaded. The root/shared namespace (empty
    // scope) loads at bootstrap, so treat it as always loaded.
    isScopeLoaded(scope: string): boolean {
        return scope === "" || this.loadedScopes.has(scope);
    }

    // A translation key was requested but not found. scopeLoaded true → a real typo
    // (warning); false → the key raced the scope load and self-heals on re-render (info).
    reportMissingKey(key: string, scope: string, scopeLoaded: boolean): void {
        Sentry.captureMessage(`i18n missing key: ${key}`, {
            level: scopeLoaded ? "warning" : "info",
            fingerprint: ["i18n-missing", key],
            tags: { i18nKey: key, i18nScope: scope },
        });
    }

    // A scope's translation file failed to load (wrong scope path, missing loader depth
    // case, 404, network drop).
    reportScopeLoadFailure(payload: LoadedEvent["payload"]): void {
        const scope = payload.scope ?? "(root)";
        Sentry.captureMessage(`i18n scope load failure: ${scope}`, {
            level: "error",
            fingerprint: ["i18n-scope-load-failure", scope],
            tags: { i18nScope: scope, i18nLang: payload.langName },
        });
    }
}
